(function () {
  const ANIMATION_DELAY_MS = 250;

  class AnnouncementBarCarousel {
    constructor(root) {
      this.root = root;
      this.slider = root.querySelector('[id^="Slider-"]');
      this.slides = Array.from(root.querySelectorAll('[id^="Slide-"]'));
      this.sliderWrapper = root.querySelector('.announcement-bar-slider');
      this.autoplayButton =
        root.querySelector('[data-announcement-autoplay]') ||
        root.closest('.utility-bar')?.querySelector('[data-announcement-autoplay]');
      this.autoRotate = this.slider?.dataset.autoRotate === 'true';
      this.speedMs = (Number(this.slider?.dataset.speed) || 5) * 1000;
      this.currentIndex = 0;
      this.autoplayTimer = null;
      this.isPlaying = false;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    }

    init() {
      if (!this.slider || this.slides.length < 2) return;

      this.setActiveSlide(0, false);
      this.autoplayButton?.addEventListener('click', () => this.toggleAutoplay());

      if (this.autoRotate && !this.reducedMotion.matches) {
        this.isPlaying = true;
        this.updateAutoplayButton();
        this.startAutoplay();
      } else if (this.autoplayButton) {
        this.isPlaying = false;
        this.updateAutoplayButton();
      }

      this.reducedMotion.addEventListener('change', () => {
        if (this.reducedMotion.matches) {
          this.stopAutoplay();
        } else if (this.autoRotate && this.isPlaying) {
          this.startAutoplay();
        }
      });
    }

    setActiveSlide(index, animate = true) {
      const direction =
        index > this.currentIndex || (this.currentIndex === this.slides.length - 1 && index === 0) ? 'next' : 'previous';
      const currentSlide = this.slides[this.currentIndex];
      const nextSlide = this.slides[index];

      if (animate && this.sliderWrapper && currentSlide && nextSlide && index !== this.currentIndex) {
        currentSlide.setAttribute('aria-hidden', 'false');
        nextSlide.setAttribute('aria-hidden', 'false');
        this.applyFadeAnimation(currentSlide, nextSlide, direction);
        window.setTimeout(() => this.updateSlideAccessibility(index), ANIMATION_DELAY_MS * 2);
      } else {
        this.updateSlideAccessibility(index);
      }

      this.currentIndex = index;
      this.slider.scrollTo({ left: nextSlide.offsetLeft, behavior: animate ? 'smooth' : 'auto' });
    }

    updateSlideAccessibility(activeIndex) {
      this.slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === activeIndex;
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        const links = slide.querySelectorAll('a');
        links.forEach((link) => link.setAttribute('tabindex', isActive ? '0' : '-1'));
      });
    }

    applyFadeAnimation(currentSlide, nextSlide, direction) {
      const outClass = `announcement-bar-slider--fade-out-${direction}`;
      const inClass = `announcement-bar-slider--fade-in-${direction}`;

      currentSlide.classList.add(outClass);
      nextSlide.classList.add(inClass);

      window.setTimeout(() => {
        currentSlide.classList.remove(outClass);
        nextSlide.classList.remove(inClass);
      }, ANIMATION_DELAY_MS * 2);
    }

    rotateNext() {
      const nextIndex = (this.currentIndex + 1) % this.slides.length;
      this.setActiveSlide(nextIndex, true);
    }

    startAutoplay() {
      this.stopAutoplay();
      this.slider?.setAttribute('aria-live', 'off');
      this.autoplayTimer = window.setInterval(() => this.rotateNext(), this.speedMs);
    }

    stopAutoplay() {
      this.slider?.setAttribute('aria-live', 'polite');
      if (this.autoplayTimer) {
        window.clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }

    toggleAutoplay() {
      this.isPlaying = !this.isPlaying;
      if (this.isPlaying) {
        this.startAutoplay();
      } else {
        this.stopAutoplay();
      }
      this.updateAutoplayButton();
    }

    updateAutoplayButton() {
      if (!this.autoplayButton) return;

      const pauseLabel = window.accessibilityStrings?.pauseSlideshow || 'Pause slideshow';
      const playLabel = window.accessibilityStrings?.playSlideshow || 'Play slideshow';

      this.autoplayButton.classList.toggle('announcement-bar__autoplay--paused', !this.isPlaying);
      this.autoplayButton.setAttribute('aria-label', this.isPlaying ? pauseLabel : playLabel);
      this.autoplayButton.setAttribute('aria-pressed', this.isPlaying ? 'false' : 'true');
    }
  }

  function initAnnouncementBars(scope = document) {
    scope.querySelectorAll('slideshow-component.announcement-bar[data-announcement-carousel]').forEach((root) => {
      if (root.dataset.announcementCarouselReady === 'true') return;
      root.dataset.announcementCarouselReady = 'true';
      new AnnouncementBarCarousel(root).init();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAnnouncementBars());
  } else {
    initAnnouncementBars();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAnnouncementBars(event.target);
  });
})();
